import {useEffect, useState} from 'react';
import {IonContent, IonPage} from '@ionic/react';
import {useHistory, useLocation} from 'react-router';
import {useRecoilValue} from 'recoil';
import {Capacitor} from '@capacitor/core';
import {Keyboard, KeyboardResize} from '@capacitor/keyboard';
import {healthDeclaration} from '../state/healthDeclaration';
import {declarationsCustomerMail} from '../state/cutomerMail';
import actions from '../api/healthDeclaration';
import HealthDeclarationQuestionType from '../types/HealthDeclaration';
import useFetchHealthDeclaration from '../hooks/useFetchHealthDeclaration';
import UIButton from '../components/UICommon/UIButton';
import UILoadingIndicator from '../components/UICommon/UILoadingIndicator';
import UIErrorIndicator from '../components/UICommon/UIErrorIndicator';
import HealthDeclarationQuestion from '../components/HealthDeclarationPage/HealthDeclarationQuestion';
import HealthDeclarationSummaryQuestion from '../components/HealthDeclarationPage/HealthDeclarationSummaryQuestion';
import HealthDeclarationSummaryHeader from '../components/HealthDeclarationPage/HealthDeclarationSummaryHeader';
import HealthDeclarationQuestionsHeader from '../components/HealthDeclarationPage/HealthDeclarationQuestionsHeader';

const HealthDeclarationPage: React.FC = () => {
  // handling customer mail
  const customerEmail = useRecoilValue(declarationsCustomerMail);

  // handling questions mechanism
  const {isLoading, isError, refetch} = useFetchHealthDeclaration();
  const declaration: any = useRecoilValue(healthDeclaration);
  const declarationQuestion: HealthDeclarationQuestionType[] = declaration?.question;
  const [totalQuestions, setTotalQuestions] = useState<any>();
  const [currentQuestion, setCurrentQuestion] = useState<any>();
  const currentIdx = declarationQuestion?.indexOf(currentQuestion);
  const [sendError, setSendError] = useState(false);
  const [isLoadingSend, setIsLoadingSend] = useState(false);
  const [showSummary, setShowSummary] = useState(false);
  const [summaryAccepted, setSummaryAccepted] = useState(false);

  // handling user answers
  const [selected, setSelected] = useState([]);
  const [selectedCheckboxes, setSelectedCheckboxes] = useState([]);
  const [fillText, setFillText] = useState<string>('');

  // handling availibity to proceed next question
  const [isNextButtonDisabled, setIsNextButtonDisabled] = useState<boolean>(false);

  const history = useHistory();
  const location = useLocation();
  const [isKeyboard, setKeyboard] = useState(0);
  const [isKeyboardOpen, setIsKeyboardOpen] = useState(false);

  if (Capacitor.isNativePlatform()) {
    Keyboard.setResizeMode({
      mode: KeyboardResize.None,
    });
    Keyboard.addListener('keyboardWillShow', (info) => {
      document?.activeElement?.scrollIntoView(true);
      setKeyboard(info.keyboardHeight);
      setIsKeyboardOpen(true);
    });

    Keyboard.addListener('keyboardWillHide', () => {
      setKeyboard(0);
      setIsKeyboardOpen(false);
    });
  }

  useEffect(() => {
    if (!declarationQuestion) {
      return;
    }
    setCurrentQuestion(declarationQuestion[0]);
    setTotalQuestions(declarationQuestion.length);
  }, [declarationQuestion]);

  useEffect(() => {
    refetch();
    setSelected([]);
    setSummaryAccepted(false);
    setShowSummary(false);
  }, [location.pathname]);

  useEffect(() => {
    setIsNextButtonDisabled(false);
    setAvailibityForNextQestion();
  }, [selectedCheckboxes, currentQuestion]);

  const setAvailibityForNextQestion = (): void => {
    if (currentQuestion?.type === 'boolean') {
      currentQuestion?.question_content.forEach((element: any) => {
        if (!selectedCheckboxes.find((answeredQuestion: any) => answeredQuestion?.id === element?.id &&
        answeredQuestion?.value)) {
          setIsNextButtonDisabled(true);
        }
      });
    }
  };

  const onNextQuestion = () => {
    const tmpSelected: any = [...selected];
    setFillText('');
    setSelectedCheckboxes([]);
    const answers = [] as any[];
    if (currentQuestion.type === 'fill') {
      currentQuestion?.question_content.forEach((element: any) => {
        answers.push({
          'answer_id': element.id,
          'answer_name': element.name,
          'content': element.text,
          'value': fillText.length > 0 && fillText !== null ? fillText : 'ofylld',
        });
      });
    }
    if (currentQuestion.type === 'boolean') {
      let answer: string;
      currentQuestion?.question_content.forEach((element: any) => {
        selectedCheckboxes?.map((el: any) => {
          if (el.id === element.id) {
            answer = el.value;
          }
        });
        answers.push({
          'answer_id': element.id,
          'answer_name': element.name,
          'content': element.text,
          'value': selectedCheckboxes?.length > 0 && selectedCheckboxes !== null ? answer : 'ofylld',
        });
      });
    }
    if (currentQuestion.type !== 'text') {
      if (tmpSelected.filter((el: any) => el.id === currentQuestion.id).length > 0) {
        for (let i = 0; i < tmpSelected.length; i++) {
          if (tmpSelected[i].id === currentQuestion.id) {
            tmpSelected[i].answers = answers;
          }
        }
      } else {
        const selectedobj = {
          'id': currentQuestion.id,
          'question': currentQuestion.title,
          'answers': answers,
          'type': currentQuestion.type,
        };
        tmpSelected.push(selectedobj);
      }
      setSelected(tmpSelected);
    }
    if (!ifLastQuestion()) {
      const nextQuestion = declarationQuestion[currentIdx + 1];
      if (tmpSelected.filter((el: any) => el.id === nextQuestion.id).length > 0) {
        const tmpChecked: any = [];
        for (let i = 0; i < tmpSelected.length; i++) {
          if (tmpSelected[i].id === nextQuestion.id) {
            for (let j = 0; j < tmpSelected[i].answers.length; j++) {
              tmpChecked.push({'id': tmpSelected[i].answers[j].answer_id, 'value': tmpSelected[i].answers[j].value});
            }
          }
        }
        setSelectedCheckboxes(tmpChecked);
      }
      setCurrentQuestion(declarationQuestion[currentIdx + 1]);
    }
    if (ifLastQuestion()) {
      setShowSummary(true);
    }
  };

  const onPreviousQuestion = () => {
    if (currentIdx === 0) {
      return;
    }
    setFillText('');
    setSelectedCheckboxes([]);
    const prevQuestion = declarationQuestion[currentIdx - 1];
    const tmpSelected: any = [...selected];
    const answers = [] as any[];
    if (currentQuestion.type === 'fill') {
      currentQuestion?.question_content.forEach((element: any) => {
        answers.push({
          'answer_id': element.id,
          'answer_name': element.name,
          'content': element.text,
          'value': fillText.length > 0 && fillText !== null ? fillText : 'ofylld',
        });
      });
    }
    if (currentQuestion.type === 'boolean') {
      let answer: string;
      currentQuestion?.question_content.forEach((element: any) => {
        selectedCheckboxes?.map((el: any) => {
          if (el.id === element.id) {
            answer = el.value;
          }
        });
        answers.push({
          'answer_id': element.id,
          'answer_name': element.name,
          'content': element.text,
          'value': selectedCheckboxes?.length > 0 && selectedCheckboxes !== null ? answer : 'ofylld',
        });
      });
    }
    if (currentQuestion.type !== 'text') {
      if (tmpSelected.filter((el: any) => el.id === currentQuestion.id).length > 0) {
        for (let i = 0; i < selected.length; i++) {
          if (tmpSelected[i].id === currentQuestion.id) {
            tmpSelected[i].answers = answers;
          }
        }
      } else {
        const selectedobj = {
          'id': currentQuestion.id,
          'question': currentQuestion.title,
          'answers': answers,
          'type': currentQuestion.type,
        };
        tmpSelected.push(selectedobj);
      }
      setSelected(tmpSelected);
    }
    if (currentIdx !== 0 ) {
      const prevQuestion = declarationQuestion[currentIdx - 1];
      if (tmpSelected.filter((el: any) => el.id === prevQuestion.id).length > 0) {
        const tmpChecked: any = [];
        for (let i = 0; i < tmpSelected.length; i++) {
          if (tmpSelected[i].id === prevQuestion.id) {
            for (let j = 0; j < tmpSelected[i].answers.length; j++) {
              tmpChecked.push({'id': tmpSelected[i].answers[j].answer_id, 'value': tmpSelected[i].answers[j].value});
            }
          }
        }
        setSelectedCheckboxes(tmpChecked);
      }
    }
    if (tmpSelected.filter((el: any) => el.id === prevQuestion.id).length > 0 && prevQuestion.type === 'fill') {
      for (let i = 0; i < selected.length; i++) {
        if (tmpSelected[i].id === prevQuestion.id) {
          setFillText(tmpSelected[i].answers[0].value);
        }
      }
    }
    if (!ifLastQuestion()) {
      setCurrentQuestion(declarationQuestion[currentIdx - 1]);
    }

    if (ifLastQuestion() && !showSummary) {
      setCurrentQuestion(declarationQuestion[currentIdx - 1]);
    }

    if (ifLastQuestion() && showSummary) {
      setShowSummary(false);
    }

    if (ifLastQuestion() && summaryAccepted) {
      setSummaryAccepted(false);
    }
  };

  const ifLastQuestion = () => {
    return currentIdx + 1 === totalQuestions;
  };

  const checkCurrentIdx = () => {
    const currentIdx = declarationQuestion.indexOf(currentQuestion) + 1;
    return currentIdx;
  };

  const handleTextareaChange = (e: any) => {
    setFillText(e.detail.value!);
  };

  const sendHealthDeclarationEmail = () => {
    setIsLoadingSend(true);
    const tmpSelected: any = [...selected];
    const data = {
      email: customerEmail,
      data: tmpSelected,
    };
    actions.sendHealthDeclaration(data).then(() => {
      history.push('/home');
    }).catch(() => {
      setIsLoadingSend(false);
      setSendError(true);
    });
    setShowSummary(false);
  };

  const acceptSummary = () => {
    setSummaryAccepted(true);
  };

  if (!declarationQuestion || !currentQuestion || isLoading || isLoadingSend) {
    return <UILoadingIndicator />;
  }

  if (isError || sendError) {
    return <UIErrorIndicator />;
  }

  return (
    <IonPage className='bg-white px-6 md:pr-10 pt-6'>
      <IonContent scrollY={isKeyboardOpen}>
        {showSummary ? (
            <HealthDeclarationSummaryHeader/>
          ) : (
            <HealthDeclarationQuestionsHeader
              headerTitle={currentQuestion?.declaration_type}
              backButtonTitle={declaration?.name}
              description={declaration?.description}
              totalSteps={totalQuestions}
              currentStep={checkCurrentIdx()}
            />
          )
        }
        <div className='flex flex-col flex-grow overflow-y-scroll h-[60%] md:h-auto'>
          {showSummary ? (
            <div className={`h-[70vh]`}>
              {selected.map((el: any) => (
                <HealthDeclarationSummaryQuestion
                  key={el.id}
                  question={el.question}
                  type={el.type}
                  answers={el.answers}
                />
              ))}
            </div>
          ) : (
            <HealthDeclarationQuestion
              title={currentQuestion.title}
              type={currentQuestion.type}
              currentQuestionId={currentQuestion.id}
              currentQuestionIdx={checkCurrentIdx()}
              content={currentQuestion.question_content}
              lastQuestion={ifLastQuestion()}
              setSelectedCheckboxes={setSelectedCheckboxes}
              selectedCheckboxes={selectedCheckboxes}
              handleTextareaChange={handleTextareaChange}
              fillText={fillText}
              selected={selected}
            />
          )}
        </div>
        <div className='bg-white fixed bottom-0 left-0 pb-6 flex-1 w-full px-5 h-[18%] md:h-auto'>
          <div className='flex flex-col-reverse md:flex-row items-center justify-center md:justify-between'>
            <div>
              {currentIdx !== 0 && <UIButton type="outline" text='Föregående fråga' onClick={onPreviousQuestion}/>}
            </div>
            {showSummary ? (
              <UIButton
                type='outline'
                text="Skicka hälsodeklaration"
                onClick={sendHealthDeclarationEmail}
                style="mb-3 md:mb-0 normal-case text-navy-primary mr-4"/>
            ) : (
              <UIButton
                type="full"
                text={ifLastQuestion() ? 'Färdig' : 'Nästa'}
                onClick={showSummary ? acceptSummary : onNextQuestion}
                style={`w-4/5 md:w-52 mb-3 md:mb-0 normal-case ${isNextButtonDisabled ? 'button-disabled' : ''}`}/>
            )}
          </div>
        </div>
        {isKeyboardOpen && <div style={{height: isKeyboard}}></div>}
      </IonContent>
    </IonPage>
  );
};

export default HealthDeclarationPage;
